import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import AppShell from "@/components/AppShell";
import { formatDate, formatApiError } from "@/lib/utils-app";
import { ReceiptIcon as Receipt, BroomIcon as Broom, ArrowsClockwiseIcon as ArrowsClockwise, EyeIcon as Eye } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useAuth } from "@/contexts/AuthContext";
import { Navigate } from "react-router-dom";
import { toast } from "sonner";

const chipFor = (t) => {
  if (t.payment_status === "paid")
    return { label: "paid", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" };
  const ageDays = (Date.now() - new Date(t.created_at).getTime()) / 86400000;
  if (t.stripe_verified_unpaid || ageDays > 7)
    return { label: "abandoned cart", cls: "bg-[#F4F2ED] text-[#8A8477] border-[#E2DAC2]", abandoned: true };
  return { label: "needs check", cls: "bg-amber-50 text-amber-700 border-amber-200", review: true };
};

export default function AdminPayments() {
  const { user } = useAuth();
  const [txs, setTxs] = useState([]);
  const [subs, setSubs] = useState([]);
  const [orphans, setOrphans] = useState([]);
  const [orphanBusy, setOrphanBusy] = useState(false);
  const [orphanMsgCount, setOrphanMsgCount] = useState(0);
  const [orphanMsgBusy, setOrphanMsgBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [reconcileBusy, setReconcileBusy] = useState(false);
  const [perRowBusy, setPerRowBusy] = useState({});
  const [showArchived, setShowArchived] = useState(false);
  const [viewTab, setViewTab] = useState("paid");

  const isAdmin = user?.role === "admin";

  // Platform revenue summary (admin-only endpoint) — month navigable.
  const [revMonth, setRevMonth] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  });
  const [revenue, setRevenue] = useState(null);
  useEffect(() => {
    if (!isAdmin) return;
    api.get("/admin/payments/revenue-summary", { params: { month: revMonth }, silent: true })
      .then((r) => setRevenue(r.data))
      .catch(() => setRevenue(null));
  }, [isAdmin, revMonth]);
  const stepMonth = (delta) => {
    const [y, m] = revMonth.split("-").map(Number);
    const d = new Date(Date.UTC(y, m - 1 + delta, 1));
    setRevMonth(`${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}`);
  };

  const loadOrphans = () =>
    api.get("/admin/files/orphans").then((r) => setOrphans(r.data.orphans || []));

  const loadOrphanMsgs = () =>
    api.get("/admin/messages/orphans").then((r) => setOrphanMsgCount(r.data.count || 0));

  useEffect(() => {
    if (!isAdmin) {
      setLoading(false);
      return;
    }
    Promise.all([
      api.get("/admin/payments", { params: { show_archived: showArchived } }).catch(() => ({ data: [] })),
      api.get("/admin/subscriptions").catch(() => ({ data: [] })),
      api.get("/admin/files/orphans").catch(() => ({ data: { orphans: [] } })),
      api.get("/admin/messages/orphans").catch(() => ({ data: { count: 0 } })),
    ])
      .then(([t, s, o, m]) => {
        // Defensive: any of these could come back as something other than
        // an array on a 5xx that escaped the retry. Always coerce to a
        // safe shape so downstream `.filter` / `.length` / `.map` never
        // crash the page.
        setTxs(Array.isArray(t?.data) ? t.data : []);
        setSubs(Array.isArray(s?.data) ? s.data : []);
        setOrphans(Array.isArray(o?.data?.orphans) ? o.data.orphans : []);
        setOrphanMsgCount(Number(m?.data?.count) || 0);
      })
      .finally(() => setLoading(false));
  }, [isAdmin, showArchived]);

  const cleanupOrphans = async () => {
    setOrphanBusy(true);
    try {
      const { data } = await api.post("/admin/files/cleanup");
      toast.success(`Soft-deleted ${data.deleted_count} orphan files`);
      await loadOrphans();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setOrphanBusy(false);
    }
  };

  const cleanupOrphanMsgs = async () => {
    setOrphanMsgBusy(true);
    try {
      const { data } = await api.post("/admin/messages/cleanup");
      toast.success(`Deleted ${data.deleted_count} orphan message${data.deleted_count === 1 ? "" : "s"}`);
      await loadOrphanMsgs();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setOrphanMsgBusy(false);
    }
  };

  const reload = async () => {
    const [t, s] = await Promise.all([
      api.get("/admin/payments", { params: { show_archived: showArchived } }),
      api.get("/admin/subscriptions"),
    ]);
    setTxs(t.data);
    setSubs(s.data);
  };

  const reconcileAll = async () => {
    setReconcileBusy(true);
    try {
      const { data } = await api.post("/admin/data/payments/reconcile", {});
      const okCount = (data.fulfilled || []).length;
      const stillCount = (data.still_unpaid || []).length;
      const errCount = (data.errors || []).length;
      if (okCount > 0) {
        toast.success(`Recovered ${okCount} stuck payment${okCount === 1 ? "" : "s"} from Stripe`);
      } else {
        toast.info(`Scanned ${data.scanned} transactions — nothing to recover. Still unpaid: ${stillCount}, errors: ${errCount}`);
      }
      // Also self-heal any subscription rows with wrong dogs_limit so
      // Pro / Kennel Elite users aren't charged for dog registrations.
      try {
        const { data: bf } = await api.post("/admin/data/subscriptions/backfill-limits");
        if ((bf.fixed_count || 0) > 0) {
          toast.success(`Fixed ${bf.fixed_count} subscription row(s) with bad allowance limits.`);
        }
      } catch {
        // Non-fatal — admin can still rerun manually.
      }
      // Finish the cleanup: hide anything Stripe just confirmed abandoned.
      await hideAbandoned();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setReconcileBusy(false);
    }
  };

  const reconcileOne = async (sessionId) => {
    setPerRowBusy((s) => ({ ...s, [sessionId]: true }));
    try {
      const { data } = await api.post("/admin/data/payments/reconcile", { session_id: sessionId });
      if ((data.fulfilled || []).length > 0) {
        toast.success("Payment fulfilled — dog/sub created.");
      } else if ((data.still_unpaid || []).length > 0) {
        toast.info(`Stripe still shows this session as ${data.still_unpaid[0].payment_status}.`);
      } else if ((data.errors || []).length > 0) {
        toast.error(`Reconcile error: ${data.errors[0].reason}`);
      }
      await reload();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setPerRowBusy((s) => ({ ...s, [sessionId]: false }));
    }
  };

  const hideAbandoned = async () => {
    try {
      const { data } = await api.post("/admin/data/payments/archive-abandoned", { older_than_days: 7 });
      if (data.archived_count > 0) toast.success(`Hid ${data.archived_count} abandoned cart${data.archived_count === 1 ? "" : "s"}.`);
      await reload();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  if (user && !isAdmin) return <Navigate to="/dashboard" replace />;

  // Defensive: if the API ever returns a wrapped object or null on a 5xx
  // retry, `.filter(...).reduce(...)` would explode and crash the page.
  // `txs` is initialized to [], but `setTxs(t.data)` could put a non-array
  // in. Cast on every render — cheap.
  const txList = Array.isArray(txs) ? txs : [];
  const totalPaid = txList
    .filter((t) => t.payment_status === "paid")
    .reduce((a, t) => a + (Number(t.amount) || 0), 0);
  const paidTxs = txList.filter((t) => chipFor(t).label === "paid");
  const reviewTxs = txList.filter((t) => chipFor(t).review);
  const shownTxs = viewTab === "paid" ? paidTxs : viewTab === "review" ? reviewTxs : txList;

  return (
    <AppShell>
      <div className="mb-8">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#6B6B6B] mb-2">Admin</p>
        <h1 className="font-spectral text-4xl sm:text-5xl tracking-tight">Payments</h1>
      </div>

      {/* Revenue first — the headline number. */}
      {revenue && (
        <div
          className="bg-ink text-white border border-gold/40 rounded-xl p-6 mb-8 shadow-[0_8px_30px_rgba(0,0,0,0.15)]"
          data-testid="revenue-summary-tile"
        >
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-gold">Your revenue</p>
              <p className="font-spectral text-4xl mt-1 tracking-tight" data-testid="revenue-total">
                ${revenue.total.toFixed(2)}
              </p>
              <p className="text-[12px] text-white/60 mt-1">
                Fees + direct sales collected via Stripe · before processing costs
              </p>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => stepMonth(-1)}
                className="h-8 w-8 rounded-full border border-white/25 hover:border-gold text-white/80 hover:text-gold transition-colors"
                data-testid="revenue-prev-month"
                aria-label="Previous month"
              >
                ‹
              </button>
              <span className="text-[13px] font-semibold font-tabular min-w-[72px] text-center" data-testid="revenue-month">
                {new Date(`${revenue.month}-15T00:00:00Z`).toLocaleDateString("en-US", { month: "short", year: "numeric", timeZone: "UTC" })}
              </span>
              <button
                onClick={() => stepMonth(1)}
                className="h-8 w-8 rounded-full border border-white/25 hover:border-gold text-white/80 hover:text-gold transition-colors"
                data-testid="revenue-next-month"
                aria-label="Next month"
              >
                ›
              </button>
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-5 pt-5 border-t border-white/15">
            {revenue.sources.map((s) => (
              <div key={s.key} data-testid={`revenue-source-${s.key}`}>
                <p className="text-[11px] uppercase tracking-[0.14em] text-white/50 font-semibold">{s.label}</p>
                <p className="font-spectral text-xl mt-0.5">${s.amount.toFixed(2)}</p>
                <p className="text-[11px] text-white/40">{s.count} {s.count === 1 ? "sale" : "sales"}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <Stat label="Total paid" value={`$${(Number(totalPaid) || 0).toFixed(2)}`} />
        <Stat label="Transactions" value={txs.length} />
        <Stat label="Active subscriptions" value={subs.length} />
        <Stat label="Orphan files" value={orphans.length} />
      </div>

      <div
        className="bg-white border border-[#E5E5E5] rounded-xl overflow-hidden shadow-[0_8px_30px_rgba(0,0,0,0.04)] mb-8"
        data-testid="orphan-files-section"
      >
        <div className="p-6 border-b border-[#E5E5E5] flex items-center justify-between gap-4">
          <div>
            <h2 className="font-spectral text-2xl">Orphan files</h2>
            <p className="text-sm text-[#6B6B6B] mt-1">
              {orphans.length === 0
                ? "No orphan files. Storage is clean."
                : `${orphans.length} file${orphans.length === 1 ? "" : "s"} not referenced by any dog.`}
            </p>
          </div>
          <Button
            disabled={orphans.length === 0 || orphanBusy}
            onClick={cleanupOrphans}
            className="bg-[#0E0E0E] hover:bg-[#C1272D] text-white rounded-full"
            data-testid="orphan-cleanup-btn"
          >
            <Broom size={14} className="mr-1" />
            {orphanBusy ? "Cleaning…" : "Clean up"}
          </Button>
        </div>
        {orphans.length > 0 && (
          <div className="max-h-64 overflow-y-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Uploaded</TableHead>
                  <TableHead>Filename</TableHead>
                  <TableHead>Size</TableHead>
                  <TableHead>Owner</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {orphans.map((o) => (
                  <TableRow key={o.id} data-testid={`orphan-row-${o.id}`}>
                    <TableCell>{formatDate(o.created_at)}</TableCell>
                    <TableCell className="font-mono text-xs">{o.original_filename}</TableCell>
                    <TableCell>{Math.round((o.size || 0) / 1024)} KB</TableCell>
                    <TableCell className="font-mono text-xs">{o.user_id?.slice(0, 8)}…</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      <div
        className="bg-white border border-[#E5E5E5] rounded-xl overflow-hidden shadow-[0_8px_30px_rgba(0,0,0,0.04)] mb-8"
        data-testid="orphan-messages-section"
      >
        <div className="p-6 flex items-center justify-between gap-4">
          <div>
            <h2 className="font-spectral text-2xl">Orphan inbox messages</h2>
            <p className="text-sm text-[#6B6B6B] mt-1">
              {orphanMsgCount === 0
                ? "No orphan messages. Every inbox link points to a live entity."
                : `${orphanMsgCount} message${orphanMsgCount === 1 ? "" : "s"} link to deleted dogs, events, or breeders.`}
            </p>
          </div>
          <Button
            disabled={orphanMsgCount === 0 || orphanMsgBusy}
            onClick={cleanupOrphanMsgs}
            className="bg-[#0E0E0E] hover:bg-[#C1272D] text-white rounded-full"
            data-testid="orphan-messages-cleanup-btn"
          >
            <Broom size={14} className="mr-1" />
            {orphanMsgBusy ? "Cleaning…" : "Clean up"}
          </Button>
        </div>
      </div>

      <div className="bg-white border border-[#E5E5E5] rounded-xl overflow-hidden shadow-[0_8px_30px_rgba(0,0,0,0.04)]">
        <div className="p-6 border-b border-[#E5E5E5]">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <h2 className="font-spectral text-2xl">Transactions</h2>
              <p className="text-sm text-[#6B6B6B] mt-1 max-w-2xl">
                Only <span className="text-emerald-700 font-medium">paid</span> rows are real money.
                Everything else is a checkout someone started and never finished — we verify those with
                Stripe automatically every night and hide abandoned carts after 7 days. No reconciling needed.
              </p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <Button
                onClick={() => setShowArchived((v) => !v)}
                variant="ghost"
                className="rounded-full"
                data-testid="toggle-archived-btn"
                title={showArchived ? "Currently showing hidden abandoned carts" : "Currently hiding archived abandoned carts"}
              >
                <Eye size={14} className="mr-1" />
                {showArchived ? "Hide archived" : "Show archived"}
              </Button>
              <Button
                onClick={reconcileAll}
                disabled={reconcileBusy}
                variant="outline"
                className="rounded-full"
                data-testid="reconcile-all-btn"
                title="Runs automatically every night — use this only if you want to re-verify with Stripe right now"
              >
                <ArrowsClockwise size={14} className="mr-1" />
                {reconcileBusy ? "Verifying…" : "Verify with Stripe now"}
              </Button>
            </div>
          </div>
          <div className="flex items-center gap-1.5 mt-4" data-testid="tx-view-tabs">
            {[
              { key: "paid", label: `Paid (${paidTxs.length})` },
              { key: "review", label: `Needs check (${reviewTxs.length})` },
              { key: "all", label: `Everything (${txList.length})` },
            ].map((tab) => (
              <button
                key={tab.key}
                onClick={() => setViewTab(tab.key)}
                className={`rounded-full px-4 py-1.5 text-[12.5px] font-semibold border transition-colors ${
                  viewTab === tab.key
                    ? "bg-ink text-white border-ink"
                    : "bg-white text-[#6B6B6B] border-[#E5E5E5] hover:border-ink"
                }`}
                data-testid={`tx-tab-${tab.key}`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>
        {loading ? (
          <div className="p-6 text-[#6B6B6B]">Loading…</div>
        ) : shownTxs.length === 0 ? (
          <div className="p-16 text-center">
            <Receipt size={48} weight="duotone" className="text-ink mx-auto mb-4" />
            <p className="text-[#6B6B6B]">
              {viewTab === "review"
                ? "Nothing needs your attention — every recent checkout is verified."
                : viewTab === "paid"
                ? "No paid transactions yet."
                : "No transactions."}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>User</TableHead>
                  <TableHead>Package</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {shownTxs.map((t) => {
                  const chip = chipFor(t);
                  return (
                    <TableRow key={t.session_id} data-testid={`tx-${t.session_id}`} className={chip.abandoned ? "opacity-60" : ""}>
                      <TableCell>{formatDate(t.created_at)}</TableCell>
                      <TableCell>{t.user_email}</TableCell>
                      <TableCell className="capitalize">{(t.package_id || "—").replace(/_/g, " ")}</TableCell>
                      <TableCell>${(Number(t.amount) || 0).toFixed(2)}</TableCell>
                      <TableCell>
                        <span className={`rounded-full px-3 py-1 text-xs font-semibold border ${chip.cls}`}>
                          {chip.label}
                        </span>
                      </TableCell>
                      <TableCell className="text-right">
                        {chip.review && (
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={perRowBusy[t.session_id]}
                            onClick={() => reconcileOne(t.session_id)}
                            className="rounded-full text-xs"
                            data-testid={`reconcile-tx-${t.session_id}`}
                          >
                            <ArrowsClockwise size={12} className="mr-1" />
                            {perRowBusy[t.session_id] ? "…" : "Verify"}
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
    </AppShell>
  );
}

function Stat({ label, value }) {
  return (
    <div className="bg-white border border-[#E5E5E5] rounded-xl p-6 shadow-[0_8px_30px_rgba(0,0,0,0.04)]">
      <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#6B6B6B] mb-2">{label}</p>
      <p className="font-spectral text-3xl tracking-tight">{value}</p>
    </div>
  );
}
